// pages/Community/SkoolPanel.jsx
//
// The Skool card inside Community → Connectors. Skool is not browser-automated
// like LinkedIn/Facebook: it has no official API, so the backend speaks its
// internal JSON API using the session the connector extension harvests. There is
// therefore no "streamed login" here — the operator just stays logged into
// skool.com and the extension syncs the cookie jar.
//
// What this panel configures:
//   • the bound community (slug + group id) and its post category
//   • the content mirror — published posts / SEO articles / FAQs, paced
//   • the weekly one-tap poll
//   • paced DM outreach into OTHER Skool communities
//
// The outreach section is deliberately opinionated. On Skool the session cookie
// is the credential for EVERY backend capability, so a spam flag would take down
// posting, the classroom and media along with the outreach — and there is no API
// key to re-provision with. Hence the visible caps and the hard requirement that
// the message template carry a personalisation token; the server enforces both
// regardless of what this form sends.
import React, { useEffect, useState, useCallback } from "react";
import addNotification from "helpers/notify";
import { getSkoolAccounts, saveSkoolAccount } from "api/connectors/connectors";

const TOKENS = ["{first_name}", "{name}", "{group}", "{bio}"];
const hasToken = (t) => TOKENS.some((k) => String(t || "").includes(k));

export default function SkoolPanel({ brandId }) {
  const [acc, setAcc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    slug: "", groupId: "", mirrorLabel: "",
    mirrorPosts: false, mirrorArticles: false, mirrorFaqs: false,
    mirrorRequirePolish: true, mirrorMaxPerDay: 6, mirrorMinGapMins: 120,
    pollsEnabled: false,
    wtpEnabled: false, problemsLabel: "",
    nudgeEnabled: false, projectsLabel: "", progressLabel: "",
    outreachEnabled: false, outreachPerDay: 5, outreachMinGapMins: 45,
    outreachTemplate: "", outreachTargets: "",
  });

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await getSkoolAccounts();
      const rows = (res && res.accounts) || [];
      const mine = rows.find((r) => String(r.brand_id) === String(brandId)) || null;
      setAcc(mine);
      if (mine) {
        const o = mine.outreach || {};
        setForm({
          slug: mine.slug || "", groupId: mine.groupId || "", mirrorLabel: mine.mirrorLabel || "",
          mirrorPosts: !!mine.mirrorPosts, mirrorArticles: !!mine.mirrorArticles, mirrorFaqs: !!mine.mirrorFaqs,
          mirrorRequirePolish: mine.mirrorRequirePolish !== false,
          mirrorMaxPerDay: mine.mirrorMaxPerDay || 6, mirrorMinGapMins: mine.mirrorMinGapMins || 120,
          pollsEnabled: !!mine.pollsEnabled,
          wtpEnabled: !!mine.wtpEnabled, problemsLabel: mine.problemsLabel || "",
          nudgeEnabled: !!mine.nudgeEnabled, projectsLabel: mine.projectsLabel || "", progressLabel: mine.progressLabel || "",
          outreachEnabled: !!o.enabled, outreachPerDay: o.perDay || 5, outreachMinGapMins: o.minGapMins || 45,
          outreachTemplate: o.template || "",
          outreachTargets: (o.targets || []).map((t) => t.slug).join("\n"),
        });
      }
    } catch (e) {
      addNotification(e?.message || "Could not load the Skool binding", "error");
    } finally { setLoading(false); }
  }, [brandId]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async () => {
    if (form.outreachEnabled && !hasToken(form.outreachTemplate)) {
      addNotification(`Outreach needs a personalised template — include one of ${TOKENS.join(" ")}`, "error");
      return;
    }
    setSaving(true);
    try {
      await saveSkoolAccount({
        brandId,
        slug: form.slug, groupId: form.groupId, mirrorLabel: form.mirrorLabel,
        mirrorPosts: form.mirrorPosts, mirrorArticles: form.mirrorArticles, mirrorFaqs: form.mirrorFaqs,
        mirrorRequirePolish: form.mirrorRequirePolish,
        mirrorMaxPerDay: Number(form.mirrorMaxPerDay), mirrorMinGapMins: Number(form.mirrorMinGapMins),
        pollsEnabled: form.pollsEnabled,
        wtpEnabled: form.wtpEnabled, problemsLabel: form.problemsLabel,
        nudgeEnabled: form.nudgeEnabled, projectsLabel: form.projectsLabel, progressLabel: form.progressLabel,
        outreach: {
          enabled: form.outreachEnabled,
          perDay: Number(form.outreachPerDay),
          minGapMins: Number(form.outreachMinGapMins),
          template: form.outreachTemplate,
          targets: String(form.outreachTargets || "")
            .split("\n").map((s) => s.trim()).filter(Boolean)
            .map((slug) => ({ slug })),
        },
      });
      addNotification("Skool settings saved", "success");
      load();
    } catch (e) {
      addNotification(e?.message || "Save failed", "error");
    } finally { setSaving(false); }
  };

  if (!brandId) return null;

  const sessionOk = acc && acc.hasCookie;
  const canDelete = acc && acc.hasClientId;

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="mb-1">
          <i className="fas fa-graduation-cap mr-2" style={{ color: "#f6c344" }} />
          Skool
        </h5>
        <p className="text-muted small mb-3">
          Skool has no official API, so this runs on the session the connector extension
          harvests — keep a logged-in skool.com tab and the extension (v0.40.0+) does the rest.
          There is no login stream for this platform.
        </p>

        <div className="mb-3 small">
          <span className={`badge badge-${sessionOk ? "success" : "secondary"} mr-2`}>
            {sessionOk ? "Session active" : "No session yet"}
          </span>
          <span className={`badge badge-${canDelete ? "success" : "warning"}`}>
            {canDelete ? "Full backend control" : "Limited — client_id not synced"}
          </span>
          {acc?.cookieNames?.length ? (
            <span className="text-muted ml-2">cookies: {acc.cookieNames.join(", ")}</span>
          ) : null}
        </div>

        <div className="form-row">
          <div className="form-group col-md-4">
            <label className="small">Community slug</label>
            <input className="form-control form-control-sm" value={form.slug} onChange={set("slug")} placeholder="free-builder-8571" />
          </div>
          <div className="form-group col-md-4">
            <label className="small">Group id</label>
            <input className="form-control form-control-sm" value={form.groupId} onChange={set("groupId")} />
          </div>
          <div className="form-group col-md-4">
            <label className="small">Post category id <span className="text-muted">(required — Skool rejects uncategorised posts)</span></label>
            <input className="form-control form-control-sm" value={form.mirrorLabel} onChange={set("mirrorLabel")} />
          </div>
        </div>

        <hr />
        <h6 className="mb-2">Content mirror</h6>
        <div className="form-row align-items-end">
          <div className="form-group col-md-2 custom-control custom-checkbox">
            <input type="checkbox" className="custom-control-input" id="sk-posts" checked={form.mirrorPosts} onChange={set("mirrorPosts")} />
            <label className="custom-control-label small" htmlFor="sk-posts">Social posts</label>
          </div>
          <div className="form-group col-md-2 custom-control custom-checkbox">
            <input type="checkbox" className="custom-control-input" id="sk-arts" checked={form.mirrorArticles} onChange={set("mirrorArticles")} />
            <label className="custom-control-label small" htmlFor="sk-arts">Articles</label>
          </div>
          <div className="form-group col-md-2 custom-control custom-checkbox">
            <input type="checkbox" className="custom-control-input" id="sk-faqs" checked={form.mirrorFaqs} onChange={set("mirrorFaqs")} />
            <label className="custom-control-label small" htmlFor="sk-faqs">FAQs</label>
          </div>
          <div className="form-group col-md-2">
            <label className="small">Per day</label>
            <input type="number" min="1" max="24" className="form-control form-control-sm" value={form.mirrorMaxPerDay} onChange={set("mirrorMaxPerDay")} />
          </div>
          <div className="form-group col-md-2">
            <label className="small">Min gap (min)</label>
            <input type="number" min="0" max="1440" className="form-control form-control-sm" value={form.mirrorMinGapMins} onChange={set("mirrorMinGapMins")} />
          </div>
          <div className="form-group col-md-2 custom-control custom-checkbox">
            <input type="checkbox" className="custom-control-input" id="sk-polish" checked={form.mirrorRequirePolish} onChange={set("mirrorRequirePolish")} />
            <label className="custom-control-label small" htmlFor="sk-polish">Polished only</label>
          </div>
        </div>
        <p className="text-muted small">
          All three sources share one daily budget and round-robin, so enabling more does not
          multiply what hits the feed. “Polished only” restricts it to content the Perfectionist
          has run — content is posted as-is, never re-edited here.
        </p>

        <hr />
        <h6 className="mb-2">Weekly poll</h6>
        <div className="custom-control custom-checkbox mb-2">
          <input type="checkbox" className="custom-control-input" id="sk-polls" checked={form.pollsEnabled} onChange={set("pollsEnabled")} />
          <label className="custom-control-label small" htmlFor="sk-polls">
            Post one poll a week (rotates through 8 questions, one tap to answer)
          </label>
        </div>

        <hr />
        <h6 className="mb-2">Willingness-to-pay poll</h6>
        <div className="custom-control custom-checkbox mb-2">
          <input type="checkbox" className="custom-control-input" id="sk-wtp" checked={form.wtpEnabled} onChange={set("wtpEnabled")} />
          <label className="custom-control-label small" htmlFor="sk-wtp">
            DM members asking what the top-voted problem would be worth solving
          </label>
        </div>
        <div className="form-group">
          <label className="small mb-1">“Problems to solve” category id</label>
          <input className="form-control form-control-sm" value={form.problemsLabel} onChange={set("problemsLabel")} placeholder="post category (label) uuid" />
        </div>
        <p className="text-muted small">
          Silent until members have actually posted problems — with an empty category nothing
          goes out, rather than asking the community to price a problem nobody raised. Asks about
          one problem at a time so the answers are comparable.
        </p>

        <hr />
        <h6 className="mb-2">Posting nudges</h6>
        <div className="custom-control custom-checkbox mb-2">
          <input type="checkbox" className="custom-control-input" id="sk-nudge" checked={form.nudgeEnabled} onChange={set("nudgeEnabled")} />
          <label className="custom-control-label small" htmlFor="sk-nudge">
            Invite a quiet member to post one line in the tab they haven’t used
          </label>
        </div>
        <div className="form-row">
          <div className="form-group col-md-6">
            <label className="small mb-1">“Projects” category id</label>
            <input className="form-control form-control-sm" value={form.projectsLabel} onChange={set("projectsLabel")} placeholder="label uuid" />
          </div>
          <div className="form-group col-md-6">
            <label className="small mb-1">“Progress” category id</label>
            <input className="form-control form-control-sm" value={form.progressLabel} onChange={set("progressLabel")} placeholder="label uuid" />
          </div>
        </div>
        <p className="text-muted small">
          No deadline and no removal threat — Discovery ranks on member growth and retention, so
          pruning a free community damages the channel most members arrive through. Each nudge
          names one specific tab, asks for a single line, and escalates at most twice before
          going quiet. Uses the “Problems to solve” id above as its third rung.
        </p>

        <hr />
        <h6 className="mb-2">Outreach DMs</h6>
        <div className="alert alert-warning small py-2">
          The session cookie is the credential for <em>everything</em> here — a spam flag would
          take down posting, the classroom and media too, and Skool has no API key to
          re-provision with. Keep volumes low and the message genuinely personal.
        </div>
        <div className="custom-control custom-checkbox mb-2">
          <input type="checkbox" className="custom-control-input" id="sk-out" checked={form.outreachEnabled} onChange={set("outreachEnabled")} />
          <label className="custom-control-label small" htmlFor="sk-out">Enable outreach</label>
        </div>
        <div className="form-row">
          <div className="form-group col-md-3">
            <label className="small">DMs per day <span className="text-muted">(max 20)</span></label>
            <input type="number" min="1" max="20" className="form-control form-control-sm" value={form.outreachPerDay} onChange={set("outreachPerDay")} />
          </div>
          <div className="form-group col-md-3">
            <label className="small">Min gap (min)</label>
            <input type="number" min="5" max="1440" className="form-control form-control-sm" value={form.outreachMinGapMins} onChange={set("outreachMinGapMins")} />
          </div>
          <div className="form-group col-md-6">
            <label className="small">Source groups <span className="text-muted">(one slug per line — you must already be a member)</span></label>
            <textarea rows={3} className="form-control form-control-sm" value={form.outreachTargets} onChange={set("outreachTargets")} placeholder={"some-saas-group\nanother-builder-group"} />
          </div>
        </div>
        <div className="form-group">
          <label className="small">
            Message template — must include one of {TOKENS.join(" ")}
          </label>
          <textarea rows={3} className="form-control form-control-sm" value={form.outreachTemplate} onChange={set("outreachTemplate")}
            placeholder="Hi {first_name} — saw you in {group}. I run a free community for people turning a skill into an AI SaaS…" />
          {form.outreachEnabled && !hasToken(form.outreachTemplate) && (
            <small className="text-danger">
              An identical message to everyone is the pattern that gets accounts flagged — add a token.
            </small>
          )}
        </div>
        <p className="text-muted small">
          Members of your own community are never contacted, and each person is messaged at most once.
        </p>

        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || loading}>
          {saving ? "Saving…" : "Save Skool settings"}
        </button>
      </div>
    </div>
  );
}
